<template>
  <div class="alert-mgr">

    <!-- ══ Header ══ -->
    <div class="alert-mgr-header">
      <div>
        <div class="alert-mgr-overline">AML Pro · Suspicious Transaction Rules</div>
        <h2 class="alert-mgr-title">Alert Management</h2>
      </div>
      <div class="d-flex align-center" style="gap:8px">
        <v-btn depressed small class="alert-btn-ghost" :loading="loading" @click="load">
          <v-icon small left>mdi-refresh</v-icon>Refresh
        </v-btn>
        <v-btn
          depressed small class="alert-btn-analyse"
          :loading="analysisRunning"
          :disabled="analysisRunning"
          @click="runAnalysis"
        >
          <v-icon small left>mdi-magnify-scan</v-icon>Run analysis
        </v-btn>
      </div>
    </div>

    <!-- ══ Stat chips ══ -->
    <div class="alert-stat-row mb-4">
      <div
        v-for="s in statChips"
        :key="s.key"
        class="alert-stat-chip"
        :class="{ 'alert-stat-chip--active': statusFilter === s.key }"
        :style="statusFilter === s.key ? `border-color:${s.color};background:${s.color}14;color:${s.color}` : ''"
        @click="setStatusFilter(s.key)"
      >
        <span class="alert-stat-chip__count" :style="statusFilter === s.key ? `color:${s.color}` : ''">{{ s.count }}</span>
        <span class="alert-stat-chip__label">{{ s.label }}</span>
      </div>
    </div>

    <!-- ══ Toolbar ══ -->
    <div class="alert-toolbar mb-3">
      <div class="d-flex align-center" style="gap:6px;flex-wrap:wrap">
        <button
          v-for="tab in ruleTabs"
          :key="tab.key"
          class="alert-tab"
          :class="{ 'alert-tab--active': ruleFilter === tab.key }"
          @click="ruleFilter = tab.key"
        >{{ tab.label }}<span class="alert-tab-count">{{ tab.count }}</span>
        </button>
      </div>
      <div class="alert-search-wrap">
        <v-icon x-small class="alert-search-icon">mdi-magnify</v-icon>
        <input v-model="search" class="alert-search-input" placeholder="Search account, name or rule…" />
      </div>
    </div>

    <!-- ══ Table ══ -->
    <div class="alert-card">
      <div class="alert-table-wrap">
        <table class="alert-table">
          <colgroup>
            <col style="width:8%">
            <col style="width:13%">
            <col style="width:14%">
            <col style="width:11%">
            <col style="width:9%">
            <col style="width:9%">
            <col style="width:10%">
            <col style="width:10%">
            <col style="width:11%">
            <col style="width:5%">
          </colgroup>
          <thead>
            <tr>
              <th>Rule</th>
              <th>Account</th>
              <th>Account holder</th>
              <th>Category</th>
              <th>Risk</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Detected</th>
              <th>Case</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading && !alerts.length">
              <td colspan="10" class="alert-empty-row">
                <v-progress-circular indeterminate size="20" width="2" color="#185FA5" class="mr-2" />
                Loading alerts…
              </td>
            </tr>
            <tr v-else-if="!filteredAlerts.length">
              <td colspan="10" class="alert-empty-row">No alerts match this filter</td>
            </tr>
            <tr
              v-for="alert in paginatedAlerts"
              :key="alert.id"
              class="alert-table-row"
              @click="openDetail(alert)"
            >
              <td>
                <span class="alert-rule-badge" :style="ruleBadgeStyle(alert)">
                  {{ alert.rule && alert.rule.ruleCode || '—' }}
                </span>
              </td>
              <td class="alert-mono">{{ alert.mainAccountNumber || '—' }}</td>
              <td class="alert-muted alert-truncate">{{ holderName(alert) }}</td>
              <td class="alert-muted">{{ alert.rule && formatCategory(alert.rule.engineCategory) || '—' }}</td>
              <td>
                <div class="d-flex align-center" style="gap:5px">
                  <div class="alert-risk-track">
                    <div class="alert-risk-bar" :style="`width:${alert.riskScore}%;background:${riskColor(alert.riskScore)}`" />
                  </div>
                  <span class="alert-risk-num" :style="`color:${riskColor(alert.riskScore)}`">{{ alert.riskScore }}</span>
                </div>
              </td>
              <td class="alert-muted">{{ alert.triggerAmount != null ? asMoney(alert.triggerAmount) : '—' }}</td>
              <td><span class="alert-status-pill" :style="statusStyle(alert.status)">{{ alert.status }}</span></td>
              <td class="alert-muted">{{ fmtDate(alert.dateCreated) }}</td>
              <td>
                <button v-if="alert.amlCase" class="alert-case-link" @click.stop="$emit('go-to-case', alert.amlCase.id)">
                  {{ alert.amlCase.caseReference }}<v-icon x-small style="margin-left:2px">mdi-open-in-new</v-icon>
                </button>
                <span v-else class="alert-muted" style="font-size:11px">No case</span>
              </td>
              <td @click.stop>
                <div class="d-flex align-center justify-end" style="gap:4px">
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <button class="alert-icon-btn" v-on="on" @click="openDetail(alert)">
                        <v-icon x-small color="#185FA5">mdi-eye</v-icon>
                      </button>
                    </template>
                    <span>View details</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="canCreateCase(alert)">
                    <template #activator="{ on }">
                      <button class="alert-icon-btn alert-icon-btn--green" v-on="on" @click="openCreateCase(alert)">
                        <v-icon x-small color="#1D9E75">mdi-folder-plus-outline</v-icon>
                      </button>
                    </template>
                    <span>Open case from alert</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="alert-table-footer">
        <span>{{ footerText }}</span>
        <div v-if="totalPages > 1" class="d-flex align-center" style="gap:6px">
          <button class="alert-page-btn" :disabled="page <= 1" @click="page--"><v-icon x-small>mdi-chevron-left</v-icon></button>
          <span style="font-size:11px;color:#777">{{ page }} / {{ totalPages }}</span>
          <button class="alert-page-btn" :disabled="page >= totalPages" @click="page++"><v-icon x-small>mdi-chevron-right</v-icon></button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════
         ALERT DETAIL DRAWER
    ══════════════════════════════════════ -->
    <v-navigation-drawer v-model="detailDrawer" app right temporary width="500" style="z-index:300">
      <div v-if="selectedAlert" class="drawer">

        <!-- Header -->
        <div class="drawer-header">
          <div>
            <div class="drawer-title">Alert Detail</div>
            <div class="drawer-sub">{{ selectedAlert.rule && selectedAlert.rule.ruleCode }} · {{ selectedAlert.rule && selectedAlert.rule.ruleName }}</div>
          </div>
          <button class="alert-icon-btn" @click="detailDrawer = false"><v-icon small>mdi-close</v-icon></button>
        </div>

        <!-- Risk hero -->
        <div class="drawer-risk-hero" :style="`background:${riskColor(selectedAlert.riskScore)}18;border-bottom:2px solid ${riskColor(selectedAlert.riskScore)}30`">
          <div>
            <div class="drawer-risk-label">Risk score</div>
            <div class="drawer-risk-value" :style="`color:${riskColor(selectedAlert.riskScore)}`">{{ selectedAlert.riskScore }}</div>
          </div>
          <div class="text-right">
            <span class="alert-status-pill" :style="statusStyle(selectedAlert.status)">{{ selectedAlert.status }}</span>
            <div class="drawer-risk-label mt-1">{{ selectedAlert.rule && formatCategory(selectedAlert.rule.engineCategory) }}</div>
          </div>
        </div>

        <div class="drawer-body">

          <!-- Account holder -->
          <div class="drawer-section-label">Account holder</div>
          <div class="drawer-account-pill mb-3">
            <div class="drawer-account-avatar" :style="avatarStyle(holderName(selectedAlert))">
              {{ avatarInitials(holderName(selectedAlert)) }}
            </div>
            <div>
              <div class="drawer-account-name">{{ holderName(selectedAlert) || '—' }}</div>
              <div class="drawer-account-sub alert-mono">{{ selectedAlert.mainAccountNumber }}</div>
            </div>
          </div>

          <!-- Rule -->
          <div class="drawer-section-label">Rule</div>
          <div class="drawer-row">
            <span class="drawer-field-label">Code</span>
            <span class="drawer-field-value"><span class="alert-rule-badge" :style="ruleBadgeStyle(selectedAlert)">{{ selectedAlert.rule && selectedAlert.rule.ruleCode }}</span></span>
          </div>
          <div class="drawer-row">
            <span class="drawer-field-label">Name</span>
            <span class="drawer-field-value">{{ selectedAlert.rule && selectedAlert.rule.ruleName || '—' }}</span>
          </div>
          <div v-if="selectedAlert.rule && selectedAlert.rule.description" class="drawer-row">
            <span class="drawer-field-label">Description</span>
            <span class="drawer-field-value" style="font-size:11px;color:#666">{{ selectedAlert.rule.description }}</span>
          </div>

          <div class="drawer-divider" />

          <!-- Trigger details -->
          <div class="drawer-section-label">Trigger details</div>
          <div v-if="selectedAlert.triggerAmount != null" class="drawer-row">
            <span class="drawer-field-label">Trigger amount</span>
            <span class="drawer-field-value" style="font-weight:600">{{ asMoney(selectedAlert.triggerAmount) }}</span>
          </div>

          <div v-if="selectedAlert.triggerDetails && selectedAlert.triggerDetails.windowMinutes != null" class="drawer-row">
            <span class="drawer-field-label">Analysis window</span>
            <span class="drawer-field-value">{{ formatWindowMinutes(selectedAlert.triggerDetails.windowMinutes) }}</span>
          </div>

          <template v-if="selectedAlert.triggerDetails">
            <div v-for="(val, key) in visibleTriggerDetails" :key="key" class="drawer-row">
              <span class="drawer-field-label">{{ formatTriggerKey(key) }}</span>
              <span class="drawer-field-value">{{ formatTriggerVal(key, val) }}</span>
            </div>
          </template>

          <div class="drawer-divider" />

          <!-- Contributing transactions -->
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="drawer-section-label" style="margin-bottom:0">Contributing transactions</div>
          </div>

          <div v-if="!alertTxns.length" class="drawer-txn-empty">
            No transactions linked to this alert. Re-run analysis to populate.
          </div>
          <div v-else>
            <div class="d-flex align-center mb-2" style="gap:6px;flex-wrap:wrap">
              <button
                v-for="f in txnFilters"
                :key="f.key"
                class="alert-tab"
                style="font-size:10px;padding:2px 8px"
                :class="{ 'alert-tab--active': txnFilter === f.key }"
                @click="txnFilter = f.key"
              >{{ f.label }}<span class="alert-tab-count">{{ f.count }}</span></button>
            </div>
            <div class="drawer-txn-list">
              <div
                v-for="t in filteredTxns"
                :key="t.id"
                class="drawer-txn-row"
              >
                <div class="d-flex align-center" style="gap:8px;min-width:0">
                  <div
                    class="drawer-txn-type-dot"
                    :style="t.transactionType === 'CREDIT' ? 'background:#1D9E75' : 'background:#E24B4A'"
                  />
                  <div style="min-width:0">
                    <div class="drawer-txn-code">{{ t.transactionCode || t.id.slice(0,8) }}</div>
                    <div class="drawer-txn-meta">{{ t.transactionMethod }} · {{ t.transactionDate }}</div>
                  </div>
                </div>
                <div class="text-right flex-shrink-0">
                  <div class="drawer-txn-amount" :style="t.transactionType === 'CREDIT' ? 'color:#1D9E75' : 'color:#E24B4A'">
                    {{ t.transactionType === 'CREDIT' ? '+' : '-' }}{{ asMoney(t.transactionAmount) }}
                  </div>
                  <div class="drawer-txn-meta">{{ t.transactionLocation || t.subAccountNumber }}</div>
                </div>
              </div>
            </div>

            <div class="drawer-txn-totals">
              <div>
                <span class="drawer-txn-total-label">Credits</span>
                <span class="drawer-txn-total-val" style="color:#1D9E75">+{{ asMoney(txnCreditTotal) }}</span>
              </div>
              <div>
                <span class="drawer-txn-total-label">Debits</span>
                <span class="drawer-txn-total-val" style="color:#E24B4A">-{{ asMoney(txnDebitTotal) }}</span>
              </div>
              <div>
                <span class="drawer-txn-total-label">Net</span>
                <span class="drawer-txn-total-val" :style="(txnCreditTotal - txnDebitTotal) >= 0 ? 'color:#1D9E75' : 'color:#E24B4A'">
                  {{ asMoney(Math.abs(txnCreditTotal - txnDebitTotal)) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Linked case -->
          <template v-if="selectedAlert.amlCase">
            <div class="drawer-divider" />
            <div class="drawer-section-label">Linked case</div>
            <div class="drawer-case-card">
              <div>
                <div class="drawer-case-ref">{{ selectedAlert.amlCase.caseReference }}</div>
                <div class="drawer-risk-label mt-1">{{ selectedAlert.amlCase.reportStage || selectedAlert.amlCase.status }}</div>
              </div>
              <button class="alert-action-btn alert-action-btn--primary" @click="$emit('go-to-case', selectedAlert.amlCase.id); detailDrawer = false">
                <v-icon x-small left>mdi-open-in-new</v-icon>View case
              </button>
            </div>
          </template>
        </div>

        <!-- Footer -->
        <div class="drawer-footer">
          <button class="alert-action-btn alert-action-btn--ghost" @click="detailDrawer = false">Close</button>
          <button
            v-if="canCreateCase(selectedAlert)"
            class="alert-action-btn alert-action-btn--green"
            @click="openCreateCase(selectedAlert); detailDrawer = false"
          >
            <v-icon x-small left>mdi-folder-plus-outline</v-icon>Open case
          </button>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- ══════════════════════════════════════
         CREATE CASE FROM ALERT DIALOG
    ══════════════════════════════════════ -->
    <v-dialog v-model="createCaseDialog" max-width="560" persistent>
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="#1D9E75">mdi-folder-plus-outline</v-icon>
            <span class="subtitle-1 font-weight-medium">Open case from alert</span>
          </div>
          <v-btn icon @click="createCaseDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="case-alert-summary mb-4" v-if="caseAlert">
            <div class="d-flex align-center mb-3" style="gap:8px">
              <div class="drawer-account-avatar" :style="avatarStyle(holderName(caseAlert))">
                {{ avatarInitials(holderName(caseAlert)) }}
              </div>
              <div>
                <div style="font-size:13px;font-weight:500">{{ holderName(caseAlert) || '—' }}</div>
                <div class="alert-mono" style="font-size:11px;color:#888">{{ caseAlert.mainAccountNumber }}</div>
              </div>
              <v-spacer />
              <span class="alert-rule-badge" :style="ruleBadgeStyle(caseAlert)">{{ caseAlert.rule && caseAlert.rule.ruleCode }}</span>
              <span class="alert-status-pill" :style="statusStyle(caseAlert.status)">{{ caseAlert.status }}</span>
            </div>
            <div class="case-alert-row">
              <span class="drawer-field-label">Rule</span>
              <span class="drawer-field-value">{{ caseAlert.rule && caseAlert.rule.ruleName }}</span>
            </div>
            <div v-if="caseAlert.triggerAmount != null" class="case-alert-row">
              <span class="drawer-field-label">Trigger amount</span>
              <span class="drawer-field-value" style="font-weight:600">{{ asMoney(caseAlert.triggerAmount) }}</span>
            </div>
            <div class="case-alert-row">
              <span class="drawer-field-label">Risk score</span>
              <span class="drawer-field-value" :style="`color:${riskColor(caseAlert.riskScore)};font-weight:600`">{{ caseAlert.riskScore }}</span>
            </div>
          </div>

          <v-form ref="caseForm">
            <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Case details</div>
            <v-select v-model="caseForm.reportType" :items="reportTypeOptions" label="Report type *" dense outlined class="mb-3" prepend-inner-icon="mdi-file-document-outline" :rules="[v => !!v || 'Required']" />
            <v-select v-model="caseForm.priority" :items="priorityOptions" label="Priority *" dense outlined class="mb-3" prepend-inner-icon="mdi-flag-outline" :rules="[v => !!v || 'Required']" />
            <v-autocomplete v-model="caseForm.assignedToUserId" :items="users" :item-text="u => u.displayName" item-value="id" label="Assign to officer *" dense outlined class="mb-3" prepend-inner-icon="mdi-account-outline" :loading="loadingUsers" no-data-text="No users found" :rules="[v => !!v || 'Required']" />
            <v-textarea v-model="caseForm.summary" label="Summary *" rows="3" dense outlined prepend-inner-icon="mdi-text" :rules="[v => !!v || 'Required']" />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createCaseDialog = false">Cancel</v-btn>
          <v-btn depressed color="#1D9E75" dark :loading="creatingCase" @click="submitCreateCase">
            <v-icon left small>mdi-folder-plus-outline</v-icon>Open case
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══════════════════════════════════════
         ANALYSIS PROGRESS DIALOG
    ══════════════════════════════════════ -->
    <AnalysisProgressDialog
      :show="showAnalysisDialog"
      :status="analysisStatus"
      :message="analysisMessage"
      :rules-evaluated="analysisRules"
      :transactions-analysed="analysisTxns"
      :alerts-created="analysisAlerts"
      @close="onAnalysisDialogClose"
    />

  </div>
</template>

<script>
import { mapState }       from 'vuex';
import FormatMixin        from '@/mixins/format.mixin';
import AmlMixin           from '@/mixins/aml.mixin';
import { AmlCaseService } from '@/modules/amlpro/services/aml-case.service';
import { UserService }    from '@/modules/global-settings/security/services/user.service';
import AnalysisProgressDialog from './AnalysisProgressDialog.vue';

const svc     = new AmlCaseService();
const userSvc = new UserService();

const PAGE_SIZE = 20;

const HIDDEN_KEYS = new Set([
  'ruleCode',
  'scope',
  'averageSource',
  'windowMinutes',
]);

const CURRENCY_KEYS = new Set([
  // shared
  'threshold',
  'actual',
  'totalAmount',
  'triggerAmount',
  // STR01, STR03 — behaviour SUM vs average
  'trendAverage',
  'historicalAvg',
  // STR04, STR06, STR07 — velocity / structuring window sum
  'windowSum',
  // STR08 — cash deposit + cheque sequence
  'cashDepositSum',
  // STR10 — loan payment sum
  'loanPaymentSum',
]);

const WINDOW_DAYS_FALLBACK = 1;

export default {
  name: 'AmlAlertManager',
  mixins: [FormatMixin, AmlMixin],
  components: { AnalysisProgressDialog },
  emits: ['go-to-case', 'case-created', 'analysis-complete'],

  data() {
    return {
      loading: false,
      alerts:  [],

      statusFilter: 'OPEN',
      ruleFilter:   'all',
      search:       '',
      page:         1,

      detailDrawer:  false,
      selectedAlert: null,

      txnLoading: false,
      alertTxns:  [],
      txnFilter:  'all',

      createCaseDialog: false,
      caseAlert:        null,
      creatingCase:     false,
      loadingUsers:     false,
      users:            [],
      caseForm: { reportType: 'STR', priority: 'MEDIUM', assignedToUserId: '', summary: '' },
      reportTypeOptions: ['STR', 'CTR', 'SAR', 'OTHER'],
      priorityOptions:   ['HIGH', 'MEDIUM', 'LOW'],

      showAnalysisDialog: false,
      analysisRunning:    false,
      analysisStatus:     'running',
      analysisMessage:    '',
      analysisRules:      0,
      analysisTxns:       0,
      analysisAlerts:     0,
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),

    statChips() {
      const countBy = (s) => this.alerts.filter(a => a.status === s).length;
      return [
        { key: 'OPEN',        label: 'Open',        count: countBy('OPEN'),        color: '#E24B4A' },
        { key: 'CASE_OPENED', label: 'Case opened', count: countBy('CASE_OPENED'), color: '#1D9E75' },
        { key: 'CLOSED',      label: 'Closed',      count: countBy('CLOSED'),      color: '#888' },
        { key: 'all',         label: 'All',         count: this.alerts.length,     color: '#534AB7' },
      ];
    },

    ruleTabs() {
      const codes = {};
      for (const a of this.alerts) {
        const code = a.rule?.ruleCode;
        if (code) codes[code] = (codes[code] ?? 0) + 1;
      }
      return [
        { key: 'all', label: 'All rules', count: this.alerts.length },
        ...Object.entries(codes)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([code, count]) => ({ key: code, label: code, count })),
      ];
    },

    filteredAlerts() {
      let list = this.alerts;
      if (this.statusFilter !== 'all') list = list.filter(a => a.status === this.statusFilter);
      if (this.ruleFilter   !== 'all') list = list.filter(a => a.rule?.ruleCode === this.ruleFilter);
      if (this.search.trim()) {
        const q = this.search.toLowerCase();
        list = list.filter(a =>
          a.mainAccountNumber?.toLowerCase().includes(q) ||
          this.holderName(a).toLowerCase().includes(q) ||
          a.rule?.ruleCode?.toLowerCase().includes(q) ||
          a.rule?.ruleName?.toLowerCase().includes(q),
        );
      }
      return list;
    },

    paginatedAlerts() {
      const start = (this.page - 1) * PAGE_SIZE;
      return this.filteredAlerts.slice(start, start + PAGE_SIZE);
    },

    totalPages() { return Math.max(1, Math.ceil(this.filteredAlerts.length / PAGE_SIZE)); },

    footerText() {
      const total = this.filteredAlerts.length;
      if (total <= PAGE_SIZE) return `${total} alert${total !== 1 ? 's' : ''}`;
      const from = (this.page - 1) * PAGE_SIZE + 1;
      const to   = Math.min(this.page * PAGE_SIZE, total);
      return `Showing ${from}–${to} of ${total} alerts`;
    },

    visibleTriggerDetails() {
      const d = this.selectedAlert?.triggerDetails;
      if (!d || typeof d !== 'object') return {};
      const out = {};
      for (const [k, v] of Object.entries(d)) {
        if (!HIDDEN_KEYS.has(k) && v != null) out[k] = v;
      }
      return out;
    },

    txnFilters() {
      const creditCount = this.alertTxns.filter(t => t.transactionType === 'CREDIT').length;
      const debitCount  = this.alertTxns.filter(t => t.transactionType === 'DEBIT').length;
      return [
        { key: 'all',    label: 'All',      count: this.alertTxns.length },
        { key: 'CREDIT', label: 'Credits',  count: creditCount },
        { key: 'DEBIT',  label: 'Debits',   count: debitCount },
      ];
    },

    filteredTxns() {
      if (this.txnFilter === 'all') return this.alertTxns;
      return this.alertTxns.filter(t => t.transactionType === this.txnFilter);
    },

    txnCreditTotal() {
      return this.alertTxns
        .filter(t => t.transactionType === 'CREDIT')
        .reduce((s, t) => s + Number(t.transactionAmount), 0);
    },

    txnDebitTotal() {
      return this.alertTxns
        .filter(t => t.transactionType === 'DEBIT')
        .reduce((s, t) => s + Number(t.transactionAmount), 0);
    },
  },

  watch: {
    statusFilter() { this.page = 1; },
    ruleFilter()   { this.page = 1; },
    search()       { this.page = 1; },
  },

  mounted() { this.load(); },

  methods: {
    async load() {
      this.loading = true;
      try {
        const res  = await svc.fetchAlerts({ limit: 200, page: 1 });
        const data = res.data ?? res;
        this.alerts = data.data ?? data;
      } catch (e) {
        console.error('AmlAlertManager.load failed:', e);
        this.showSnack?.({ color: 'error', title: 'Error', message: 'Failed to load alerts' });
      } finally {
        this.loading = false;
      }
    },

    async runAnalysis() {
      this.analysisStatus  = 'running';
      this.analysisMessage = 'Evaluating active rules against imported transactions…';
      this.analysisRules   = 0;
      this.analysisTxns    = 0;
      this.showAnalysisDialog = true;
      this.analysisRunning    = true;

      try {
        const res    = await svc.runAnalysis();
        const result = res.data ?? res;
        this.analysisRules  = result.rulesEvaluated      ?? 0;
        this.analysisTxns   = result.transactionsAnalysed ?? 0;
        this.analysisAlerts = result.alertsCreated        ?? 0;
        this.analysisStatus = 'done';
        this.analysisMessage =
          `${this.analysisRules} rule${this.analysisRules !== 1 ? 's' : ''} evaluated · ` +
          `${this.analysisTxns} unique transaction${this.analysisTxns !== 1 ? 's' : ''} analysed · ` +
          `${this.analysisAlerts} alert${this.analysisAlerts !== 1 ? 's' : ''} created`;

        await this.load();

        this.$emit('case-created', null);
        this.$emit('analysis-complete');
      } catch (e) {
        this.analysisStatus  = 'error';
        this.analysisMessage = e?.response?.data?.message ?? 'Failed to run analysis';
      } finally {
        this.analysisRunning = false;
      }
    },

    onAnalysisDialogClose() {
      if (this.analysisRunning) return;
      this.showAnalysisDialog = false;
    },

    setStatusFilter(key) { this.statusFilter = key; this.page = 1; },

    async openDetail(alert) {
      this.selectedAlert = alert;
      this.txnFilter     = 'all';
      this.detailDrawer  = true;
      this.alertTxns = (alert.transactions ?? []).slice().sort(
        (a, b) => new Date(b.transactionDate || b.dateCreated) - new Date(a.transactionDate || a.dateCreated),
      );
    },

    canCreateCase(alert) {
      return alert.status === 'OPEN' && !alert.amlCase;
    },

    async openCreateCase(alert) {
      this.caseAlert = alert;
      this.caseForm  = {
        reportType:       'STR',
        priority:         alert.riskScore >= 75 ? 'HIGH' : 'MEDIUM',
        assignedToUserId: this.loggedInUser?.id ?? '',
        summary: `${alert.rule?.ruleCode} — ${alert.rule?.ruleName}. ` +
                 `Account ${alert.mainAccountNumber}` +
                 (this.holderName(alert) ? ` (${this.holderName(alert)})` : '') + '. ' +
                 (alert.triggerAmount != null ? `Trigger amount: ${this.asMoney(alert.triggerAmount)}.` : ''),
      };
      this.createCaseDialog = true;
      if (!this.users.length) await this.loadUsers();
    },

    async loadUsers() {
      this.loadingUsers = true;
      try {
        const res  = await userSvc.findAll();
        const data = res.data ?? res;
        const list = data.users ?? data;
        this.users = list.map(u => ({
          id: u.id,
          displayName: u.person
            ? this.capitalizeName(`${u.person.firstName ?? ''} ${u.person.lastName ?? ''}`.trim()) || u.username
            : u.username,
        }));
      } catch {
        this.showSnack?.({ color: 'error', title: 'Error', message: 'Failed to load users' });
      } finally {
        this.loadingUsers = false;
      }
    },

    async submitCreateCase() {
      if (!this.$refs.caseForm.validate()) return;
      this.creatingCase = true;
      try {
        const res    = await svc.create({
          accountId:        this.caseAlert.accountId,
          amlAlertId:       this.caseAlert.id,
          reportType:       this.caseForm.reportType,
          priority:         this.caseForm.priority,
          assignedToUserId: this.caseForm.assignedToUserId,
          summary:          this.caseForm.summary,
        });
        const result = res.data ?? res;

        const idx = this.alerts.findIndex(a => a.id === this.caseAlert.id);
        if (idx !== -1) {
          this.alerts[idx] = {
            ...this.alerts[idx],
            status:  'CASE_OPENED',
            amlCase: { id: result.id, caseReference: result.caseReference, reportStage: result.reportStage, status: result.status },
          };
          this.alerts = [...this.alerts];
        }

        this.showSnack?.({ color: 'success', title: 'Case opened', message: `Case ${result.caseReference} created from alert` });
        this.createCaseDialog = false;
        this.$emit('case-created', result);
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to create case';
        this.showSnack?.({ color: 'error', title: 'Error', message: Array.isArray(msg) ? msg.join(', ') : msg });
      } finally {
        this.creatingCase = false;
      }
    },

    holderName(alert) {
      const personRows = alert.account?.person;
      if (Array.isArray(personRows) && personRows.length) {
        const primary = personRows.find(r => r.personClass === 'PRIMARY' || r.personClass === 'MEMBER') ?? personRows[0];
        const p = primary?.person ?? primary;
        if (p?.firstName || p?.lastName) {
          return this.capitalizeName(`${p.firstName ?? ''} ${p.lastName ?? ''}`.trim());
        }
      }
      if (alert.holderName) return this.capitalizeName(alert.holderName);
      if (alert.triggerDetails?.personName) return this.capitalizeName(alert.triggerDetails.personName);
      return '';
    },

    avatarInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    },

    avatarStyle(name) {
      const palettes = [
        'background:#B5D4F4;color:#185FA5',
        'background:#9FE1CB;color:#0F6E56',
        'background:#CECBF6;color:#3C3489',
        'background:#F5C4B3;color:#993C1D',
        'background:#FAC775;color:#854F0B',
      ];
      return palettes[(name ? name.charCodeAt(0) : 0) % palettes.length];
    },

    fmtDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    riskColor(score) {
      if (score >= 75) return '#E24B4A';
      if (score >= 50) return '#EF9F27';
      return '#1D9E75';
    },

    ruleBadgeStyle(alert) {
      const map = {
        VELOCITY:    'background:#E6F1FB;color:#185FA5',
        BEHAVIOUR:   'background:#EEEDFE;color:#534AB7',
        STRUCTURING: 'background:#FAEEDA;color:#854F0B',
        THRESHOLD:   'background:#F1EFE8;color:#5F5E5A',
      };
      return map[alert.rule?.engineCategory] || 'background:#F1EFE8;color:#5F5E5A';
    },

    statusStyle(status) {
      const map = {
        OPEN:        'background:#FCEBEB;color:#A32D2D',
        CASE_OPENED: 'background:#E1F5EE;color:#0F6E56',
        DISMISSED:   'background:#FAEEDA;color:#854F0B',
        CLOSED:      'background:#F1EFE8;color:#5F5E5A',
      };
      return map[status] || 'background:#F1EFE8;color:#5F5E5A';
    },

    formatCategory(cat) {
      return { VELOCITY: 'Velocity', BEHAVIOUR: 'Behaviour', STRUCTURING: 'Structuring', THRESHOLD: 'Threshold' }[cat] || cat || '—';
    },

    formatTriggerKey(key) {
      return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).replace(/_/g, ' ');
    },

    formatTriggerVal(key, val) {
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      if (Array.isArray(val))       return val.join(', ');
      if (typeof val === 'number' && CURRENCY_KEYS.has(key)) return this.asMoney(val);
      return String(val);
    },

    formatWindowMinutes(minutes) {
      const m = Number(minutes);
      if (!m || isNaN(m)) return '—';
      const days  = Math.floor(m / 1440);
      const hours = Math.floor((m % 1440) / 60);
      const mins  = m % 60;
      const parts = [];
      if (days)  parts.push(`${days} day${days  !== 1 ? 's' : ''}`);
      if (hours) parts.push(`${hours} hr${hours !== 1 ? 's' : ''}`);
      if (mins)  parts.push(`${mins} min${mins  !== 1 ? 's' : ''}`);
      return parts.join(' ') || '—';
    },
  },
};
</script>

<style scoped>
.alert-mgr { font-family: inherit; }

/* Header */
.alert-mgr-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:20px; flex-wrap:wrap; gap:8px; }
.alert-mgr-overline { font-size:11px; font-weight:600; letter-spacing:1px; text-transform:uppercase; color:#999; margin-bottom:4px; }
.alert-mgr-title { font-size:22px; font-weight:500; letter-spacing:-0.3px; margin:0; }

/* Buttons */
.alert-btn-ghost { font-size:12px !important; border:0.5px solid #ddd !important; background:transparent !important; color:inherit !important; box-shadow:none !important; }
.alert-btn-analyse { font-size:12px !important; background:#1D9E75 !important; color:#fff !important; box-shadow:none !important; }

/* Stat chips */
.alert-stat-row { display:flex; gap:8px; flex-wrap:wrap; }
.alert-stat-chip { display:flex; align-items:center; gap:8px; padding:8px 14px; border-radius:10px; border:0.5px solid #e0e0e0; background:white; cursor:pointer; transition:all 0.12s; }
.alert-stat-chip:hover { background:#f7f9fc; }
.alert-stat-chip--active { border-width:1.5px; }
.alert-stat-chip__count { font-size:18px; font-weight:600; line-height:1; }
.alert-stat-chip__label { font-size:11px; color:#888; white-space:nowrap; }

/* Toolbar */
.alert-toolbar { display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }

/* Tabs */
.alert-tab { font-size:11px; padding:4px 10px; border-radius:20px; border:0.5px solid #ddd; background:transparent; color:#666; cursor:pointer; transition:all 0.12s; display:inline-flex; align-items:center; gap:5px; white-space:nowrap; font-family:inherit; }
.alert-tab:hover { background:#f5f5f5; }
.alert-tab--active { background:#185FA5; color:#fff; border-color:#185FA5; font-weight:600; }
.alert-tab-count { font-size:10px; font-weight:700; padding:1px 5px; border-radius:10px; background:rgba(255,255,255,0.25); }

/* Search */
.alert-search-wrap { position:relative; }
.alert-search-icon { position:absolute; left:8px; top:50%; transform:translateY(-50%); color:#bbb; }
.alert-search-input { font-size:12px; padding:5px 10px 5px 26px; border:0.5px solid #ddd; border-radius:8px; background:white; color:inherit; width:200px; outline:none; font-family:inherit; }
.alert-search-input:focus { border-color:#999; }

/* Card + table */
.alert-card { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; }
.alert-table-wrap { overflow-x:auto; }
.alert-table { width:100%; border-collapse:collapse; font-size:12px; table-layout:fixed; }
.alert-table thead tr { border-bottom:0.5px solid #e0e0e0; }
.alert-table th { padding:9px 14px; text-align:left; font-size:10px; font-weight:600; color:#aaa; letter-spacing:.5px; text-transform:uppercase; }
.alert-table-row { border-bottom:0.5px solid #e0e0e0; cursor:pointer; transition:background 0.1s; }
.alert-table-row:hover { background:#EBF3FC; }
.alert-table td { padding:10px 14px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.alert-empty-row { padding:32px 20px; text-align:center; color:#aaa; font-size:13px; }
.alert-table-footer { padding:9px 16px; border-top:0.5px solid #e0e0e0; font-size:11px; color:#aaa; display:flex; align-items:center; justify-content:space-between; }

/* Cell helpers */
.alert-muted    { color:#777; }
.alert-mono     { font-family:monospace; font-size:11px; }
.alert-truncate { max-width:140px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.alert-rule-badge { display:inline-block; padding:2px 7px; border-radius:5px; font-size:10px; font-weight:700; }
.alert-status-pill { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:600; white-space:nowrap; }
.alert-risk-track { width:48px; height:4px; border-radius:4px; background:#eee; overflow:hidden; flex-shrink:0; }
.alert-risk-bar   { height:100%; border-radius:4px; }
.alert-risk-num   { font-size:11px; font-weight:600; }
.alert-case-link  { font-size:11px; color:#185FA5; font-weight:600; background:none; border:none; cursor:pointer; padding:0; font-family:monospace; display:inline-flex; align-items:center; gap:2px; }
.alert-case-link:hover { text-decoration:underline; }

/* Icon buttons */
.alert-icon-btn { width:26px; height:26px; border-radius:6px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; padding:0; transition:background 0.1s; }
.alert-icon-btn:hover { background:#f0f0f0; }
.alert-icon-btn--green { border-color:#9FE1CB; }
.alert-icon-btn--green:hover { background:#E1F5EE; }

/* Pagination */
.alert-page-btn { width:24px; height:24px; border-radius:6px; border:0.5px solid #ddd; background:transparent; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; padding:0; }
.alert-page-btn:hover:not(:disabled) { background:#f0f0f0; }
.alert-page-btn:disabled { opacity:0.35; cursor:not-allowed; }

/* Drawer */
.drawer { display:flex; flex-direction:column; height:100%; font-family:inherit; }
.drawer-header { display:flex; align-items:flex-start; justify-content:space-between; padding:16px 16px 12px; border-bottom:0.5px solid #e0e0e0; flex-shrink:0; }
.drawer-title  { font-size:15px; font-weight:600; }
.drawer-sub    { font-size:11px; color:#888; margin-top:2px; }
.drawer-risk-hero { padding:16px 20px; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
.drawer-risk-label { font-size:10px; font-weight:600; letter-spacing:.5px; text-transform:uppercase; color:#999; }
.drawer-risk-value { font-size:36px; font-weight:700; line-height:1; }
.drawer-body { flex:1; overflow-y:auto; padding:16px; }
.drawer-section-label { font-size:10px; font-weight:600; letter-spacing:.5px; text-transform:uppercase; color:#aaa; margin-bottom:8px; }
.drawer-row { display:flex; justify-content:space-between; align-items:flex-start; padding:6px 0; border-bottom:0.5px solid #f5f5f5; gap:12px; }
.drawer-row:last-child { border-bottom:none; }
.drawer-field-label { font-size:11px; color:#999; flex-shrink:0; }
.drawer-field-value { font-size:12px; color:#1a1a1a; text-align:right; word-break:break-word; }
.drawer-divider { height:0.5px; background:#e0e0e0; margin:12px 0; }

/* Account pill in drawer */
.drawer-account-pill { display:flex; align-items:center; gap:10px; background:#EBF3FC; border-radius:10px; padding:10px 12px; }
.drawer-account-avatar { width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; font-family:monospace; flex-shrink:0; }
.drawer-account-name { font-size:13px; font-weight:500; }
.drawer-account-sub  { font-size:11px; color:#888; margin-top:2px; }

/* Transaction list */
.drawer-txn-empty { font-size:12px; color:#aaa; text-align:center; padding:16px; background:#f7f9fc; border-radius:8px; margin-bottom:8px; }
.drawer-txn-list { border:0.5px solid #e0e0e0; border-radius:10px; overflow:hidden; max-height:320px; overflow-y:auto; }
.drawer-txn-row { display:flex; align-items:center; justify-content:space-between; padding:10px 12px; border-bottom:0.5px solid #f0f0f0; gap:10px; }
.drawer-txn-row:last-child { border-bottom:none; }
.drawer-txn-row:hover { background:#f7f9fc; }
.drawer-txn-type-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
.drawer-txn-code { font-size:11px; font-weight:600; font-family:monospace; }
.drawer-txn-meta { font-size:10px; color:#aaa; margin-top:2px; }
.drawer-txn-amount { font-size:12px; font-weight:600; }
.drawer-txn-totals { display:flex; justify-content:space-between; background:#f7f9fc; border-radius:0 0 10px 10px; border:0.5px solid #e0e0e0; border-top:none; padding:8px 12px; margin-top:-1px; }
.drawer-txn-total-label { font-size:10px; color:#aaa; display:block; margin-bottom:2px; text-transform:uppercase; letter-spacing:.5px; }
.drawer-txn-total-val { font-size:12px; font-weight:700; }

/* Case card in drawer */
.drawer-case-card { display:flex; align-items:center; justify-content:space-between; background:#E1F5EE; border-radius:10px; padding:12px 14px; }
.drawer-case-ref  { font-size:13px; font-weight:600; color:#0F6E56; font-family:monospace; }
.drawer-footer { padding:12px 16px; border-top:0.5px solid #e0e0e0; display:flex; justify-content:flex-end; gap:8px; flex-shrink:0; }

/* Action buttons */
.alert-action-btn { font-size:12px; padding:5px 12px; border-radius:8px; border:0.5px solid #ddd; background:transparent; color:inherit; cursor:pointer; display:inline-flex; align-items:center; gap:4px; transition:all 0.12s; font-family:inherit; }
.alert-action-btn:hover { background:#f5f5f5; }
.alert-action-btn--primary { background:#185FA5; color:#fff; border-color:#185FA5; }
.alert-action-btn--primary:hover { background:#0C447C; }
.alert-action-btn--green { background:#1D9E75; color:#fff; border-color:#1D9E75; }
.alert-action-btn--green:hover { background:#17785A; }
.alert-action-btn--ghost { background:#f5f5f5; color:#555; }

/* Create case dialog */
.case-alert-summary { background:#f7f9fc; border:0.5px solid #e0e0e0; border-radius:10px; padding:14px; }
.case-alert-row { display:flex; justify-content:space-between; align-items:center; padding:4px 0; border-bottom:0.5px solid #f0f0f0; gap:12px; }
.case-alert-row:last-child { border-bottom:none; }
</style>